/**
 * OfflineBanner — Wave 42.169
 *
 * Renders a dismiss-able pill at the top of the page whenever the
 * browser reports it's offline, so users understand why their next
 * click "does nothing" instead of blaming the app.
 *
 * Non-invasive: no localStorage, no Redux — just a tiny event listener
 * on window.online / window.offline. Auto-hides when connectivity
 * returns. Positioned as `fixed top-0` with a high z-index so it sits
 * above any dashboard chrome without breaking layouts.
 */
import React, { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';

const OfflineBanner = () => {
  const [offline, setOffline] = useState(
    typeof navigator !== 'undefined' && navigator.onLine === false,
  );

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  if (!offline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
      className="fixed top-0 left-1/2 -translate-x-1/2 z-[9999] mt-3 px-4 py-2 rounded-full text-sm font-semibold bg-amber-900 text-amber-50 shadow-lg flex items-center gap-2"
    >
      <WifiOff className="h-4 w-4" />
      You&apos;re offline — changes will retry when you reconnect.
    </div>
  );
};

export default OfflineBanner;
